/* eslint-disable no-restricted-syntax -- Not translatable strings */
import { useVariableNode } from "@circle-react-shared/uikit/TipTap/Extensions/CustomExtensions/Variable/hooks/useVariableNode";
import { IconButton } from "@circleco/compass/components/IconButton";
import { Menu } from "@circleco/compass/components/Menu";
import type { MenuOptions } from "@circleco/compass/components/Menu";
import type { Editor } from "@tiptap/react";
import noop from "lodash/noop";
import { useEffect, useMemo, useRef, useState } from "react";

import { t } from "@/i18n-js/instance";

import type { Variable } from "../InsertVariableButton/InsertVariableButtonWrapper";
import { useTextSource } from "../InsertVariableButton/useTextSource";

const DROPDOWN_TRIGGER_INPUT_TEXT = "{{";

const URL_VARIABLES = new Set([
  "{{community.url}}",
  "{{trigger.event.url}}",
  "{{trigger.event.space.url}}",
  "{{trigger.event.space.space_group.url}}",
  "{{trigger.comment.url}}",
  "{{trigger.comment.post.url}}",
  "{{trigger.comment.post.space.url}}",
  "{{trigger.comment.post.space.space_group.url}}",
  "{{trigger.post.url}}",
  "{{trigger.post.space.url}}",
  "{{trigger.post.space.space_group.url}}",
]);

const buildMenuOptions = (
  variables: Variable[],
  onSelect: (value: string) => void,
): MenuOptions => {
  const groups = new Map<string, Variable[]>();
  variables.forEach(variable => {
    const list = groups.get(variable.group) ?? [];
    list.push(variable);
    groups.set(variable.group, list);
  });

  return Array.from(groups.entries()).map(([groupLabel, items]) => ({
    label: groupLabel,
    items: items.map(variable => ({
      label: variable.label,
      onClick: () => onSelect(variable.value),
    })),
  }));
};

interface InsertVariableButtonV4Props {
  editor?: Editor;
  enableLinkInsertion?: boolean;
  inputRef?: React.RefObject<HTMLInputElement | null>;
  setInputChangeHandler?: (handler: () => void) => void;
  variables: Variable[];
}

export const InsertVariableButtonV4 = ({
  editor,
  inputRef,
  setInputChangeHandler = noop,
  variables,
  enableLinkInsertion = false,
}: InsertVariableButtonV4Props) => {
  const textSource = useTextSource({ editor, inputRef });
  const { onInsertVariable } = useVariableNode({ editor, variables });
  const isLinkInsertionSupported = Boolean(editor) && enableLinkInsertion;
  const [isOpen, setIsOpen] = useState(false);

  const textSourceRef = useRef(textSource);
  textSourceRef.current = textSource;
  const setInputChangeHandlerRef = useRef(setInputChangeHandler);
  setInputChangeHandlerRef.current = setInputChangeHandler;
  const isLinkInsertionSupportedRef = useRef(isLinkInsertionSupported);
  isLinkInsertionSupportedRef.current = isLinkInsertionSupported;

  const getLastTwoChars = () =>
    textSourceRef.current?.textUpToAnchor().slice(-2) ?? "";

  const insertVariableRef = useRef((variableName: string) => {
    const source = textSourceRef.current;
    if (!source) return;

    const shouldSliceVariableName =
      getLastTwoChars() === DROPDOWN_TRIGGER_INPUT_TEXT;
    const textToInsert = shouldSliceVariableName
      ? variableName.slice(2)
      : variableName;

    if (
      isLinkInsertionSupportedRef.current &&
      URL_VARIABLES.has(variableName)
    ) {
      source.insertLink(variableName, shouldSliceVariableName);
    } else if (!onInsertVariable(variableName)) {
      source.insertText(textToInsert);
    }
  });

  useEffect(() => {
    const handleContentChange = () => {
      if (getLastTwoChars() === DROPDOWN_TRIGGER_INPUT_TEXT) {
        setIsOpen(true);
      }
    };

    if (editor) {
      editor.on("update", handleContentChange);
      return () => {
        editor.off("update", handleContentChange);
      };
    }

    setInputChangeHandlerRef.current(handleContentChange);
    return () => setInputChangeHandlerRef.current(noop);
  }, [editor]);

  const menuOptions = useMemo<MenuOptions>(
    () =>
      buildMenuOptions(variables, value => insertVariableRef.current(value)),
    [variables],
  );

  return (
    <Menu
      open={isOpen}
      onOpenChange={setIsOpen}
      options={menuOptions}
      trigger={
        <IconButton
          icon="brackets"
          variant="ghost"
          size="sm"
          aria-label={t("tiptap.add_variable")}
        />
      }
    />
  );
};
